import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { toast } from "react-toastify";
import { LoadingComponent, UserHover } from "Components";
import { CopyTemplate } from "Components/Modal";
import { EmailTemplateForm } from "./EmailTemplateForm";
import { authService, newAuthService } from "Service/Axios";
import {
  COPY_EMAIL_TEMPLATE_URL,
  CREATE_EMAIL_TEMPLATE_URL,
  DELETE_EMAIL_TEMPLATE_URL,
  EDIT_EMAIL_TEMPLATE_URL,
  GET_EMAIL_TEMPLATE_DETAILS_URL,
  GET_EMAIL_TEMPLATE_LIST_BY_CATEGORY_URL,
} from "Constant/api";
import {
  useEmailTemplateCategoryList,
  useQuestionList,
} from "Constant/apiHooks";
import { ISO_DATE } from "Constant/constants";
import {
  downloadFile,
  formatDateWithShortMonth,
  getFirstLetterOfName,
} from "Utils";

export const EmailTemplate = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [isCopyTemplate, setIsCopyTemplate] = useState(false);
  const [formMode, setFormMode] = useState(null);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [editTemplateData, setEditTemplateData] = useState(null);
  const [copyTemplateData, setCopyTemplateData] = useState(null);
  const [emailTemplateList, setEmailTemplateList] = useState([]);

  const {
    categoryList,
    loading: categoryLoading,
    activeCategory,
    setActiveCategory,
    fetchCategoryList,
  } = useEmailTemplateCategoryList();
  const { questionList, loading: questionLoading } = useQuestionList();

  //Toggle
  const toggleCategory = (category) => {
    setActiveCategory(category);
  };

  const toggleNewTemplate = () => {
    if (formMode === "create") {
      setFormMode(null);
      setActiveTemplate(null);
      setEditTemplateData(null);
    } else {
      setFormMode("create");
      setActiveTemplate(null);
      setEditTemplateData(null);
    }
  };

  const toggleCopyTemplate = (template) => {
    setIsCopyTemplate(!isCopyTemplate);
    if (template) {
      setCopyTemplateData(template);
    }
  };

  // Listing
  const variableList = useMemo(() => {
    return questionList
      ?.map((question) => question.name)
      .sort((a, b) => (a.toLowerCase() > b.toLowerCase() ? 1 : -1));
  }, [questionList]);

  const variableListForEmailEdtor = useMemo(() => {
    return questionList?.map((question) => ({
      name: question.name,
      value: `{${question.name}}`,
    }));
  }, [questionList]);

  const getEmailTemplateList = async (category) => {
    if (category) {
      setLoading(true);
      const payload = {
        category,
      };
      const response = await newAuthService(
        GET_EMAIL_TEMPLATE_LIST_BY_CATEGORY_URL,
        "POST",
        payload
      );
      setEmailTemplateList(response.data);
      setLoading(false);
    }
  };

  useEffect(() => {
    getEmailTemplateList(activeCategory);
  }, [activeCategory]);

  //On Click
  const onViewTemplate = async (id) => {
    setLoading(true);
    const data = {
      gid: id,
    };
    const response = await newAuthService(
      GET_EMAIL_TEMPLATE_DETAILS_URL,
      "POST",
      data
    );
    if (
      response.status // && response.data.success
    ) {
      setActiveTemplate(response.data);
      setLoading(false);
    } else {
      toast.error(t("common.messages.notification.failed.somethingWentWrong"));
      setLoading(false);
    }
  };

  const onResetTemplate = () => {
    setActiveTemplate(null);
    setCopyTemplateData(null);
    setFormMode(null);
    setEditTemplateData(null);
  };

  const onEditTemplate = (template) => {
    setFormMode("edit");
    setEditTemplateData({
      templateName: template.name,
      templateDescription: template.description,
      templateCategory: template.category,
      templateContent: template.template,
      templateAttachment: template.files,
      templateWishList: template.wishlist,
      templateType: template.type,
      templateJson: JSON.parse(template.jsondata),
    });
  };

  const onAddTemplate = async (formData) => {
    try {
      setButtonLoading(true);
      const data = {
        category: formData.templateCategory,
        name: formData.templateName,
        template: formData.templateContent,
        description: formData.templateDescription,
        creationDate: ISO_DATE,
        files: formData.templateAttachment,
        wishlist: false,
        type: formData.templateType,
        jsondata: JSON.stringify(formData.templateJson),
        project: formData.projectId,
      };
      const response = await newAuthService(
        CREATE_EMAIL_TEMPLATE_URL,
        "POST",
        data
      );
      if (response.status && response.data.success) {
        fetchCategoryList();
        getEmailTemplateList(activeCategory);
        onResetTemplate();
        toast.success(
          t("common.messages.notification.success.saveEmailTemplate")
        );
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(t("common.messages.notification.failed.somethingWentWrong"));
    } finally {
      setButtonLoading(false);
    }
  };

  const onSaveTemplate = async (formData) => {
    try {
      setButtonLoading(true);
      const data = {
        gid: activeTemplate.gid,
        category: formData.templateCategory,
        name: formData.templateName,
        template: formData.templateContent,
        description: formData.templateDescription,
        updationDate: ISO_DATE,
        files: formData.templateAttachment,
        wishlist: formData.templateWishList,
        type: formData.templateType,
        jsondata: JSON.stringify(formData.templateJson),
        project: formData.projectId,
      };
      const response = await newAuthService(
        EDIT_EMAIL_TEMPLATE_URL,
        "POST",
        data
      );
      if (response.status && response.data.success) {
        fetchCategoryList();
        getEmailTemplateList(activeCategory);
        onResetTemplate();
        toast.success(
          t("common.messages.notification.success.saveEmailTemplate")
        );
      } else {
        toast.error(response.data.message);
      }
    } catch {
      toast.error(t("common.messages.notification.failed.somethingWentWrong"));
    } finally {
      setButtonLoading(false);
    }
  };

  const onWishListTemplate = async (workflow) => {
    try {
      const data = {
        gid: workflow.gid,
        category: workflow.category,
        name: workflow.name,
        template: workflow.template,
        description: workflow.description,
        updationDate: ISO_DATE,
        files: workflow.files,
        wishlist: !workflow.wishlist,
        type: workflow.type,
        jsondata: workflow.jsondata,
      };
      const response = await newAuthService(
        EDIT_EMAIL_TEMPLATE_URL,
        "POST",
        data
      );
      if (response.status && response.data.success) {
        fetchCategoryList();
        getEmailTemplateList(activeCategory);
        onResetTemplate();
        toast.success(
          t("common.messages.notification.success.wishListEmailTemplate")
        );
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(t("common.messages.notification.failed.somethingWentWrong"));
    }
  };

  const onCopyTemplate = async (tData) => {
    try {
      const data = {
        category: tData.templateCategory,
        name: tData.templateName,
        gid: copyTemplateData.gid,
      };
      const response = await authService(COPY_EMAIL_TEMPLATE_URL, "POST", data);
      if (response.status && response.data.success) {
        fetchCategoryList();
        getEmailTemplateList(activeCategory);
        onResetTemplate();
        toast.success(
          t("common.messages.notification.success.copyEmailTemplate")
        );
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(t("common.messages.notification.failed.somethingWentWrong"));
    }
  };

  const onDeleteTemplate = async (id) => {
    try {
      const data = {
        gid: id,
      };
      const response = await newAuthService(
        DELETE_EMAIL_TEMPLATE_URL,
        "POST",
        data
      );
      if (response.status && response.data.success) {
        fetchCategoryList();
        getEmailTemplateList(activeCategory);
        toast.success(
          t("common.messages.notification.success.deleteEmailTemplate")
        );
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(t("common.messages.notification.failed.somethingWentWrong"));
    }
  };

  return (
    <LoadingComponent isLoading={loading || categoryLoading || questionLoading}>
      <div className="">
        {/* Email Template List View */}
        {!formMode && !activeTemplate && (
          <div className="email-nasting p-0">
            <div className="email-search p-4 bg-slate-50">
              <div className="row">
                <div className="col pe-2">
                  <div className="search-div">
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="pluto-search"></i>
                      </span>
                      <input
                        type="search"
                        className="form-control"
                        placeholder={t("common.placeHolder.searchTemplate")}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-auto ps-2">
                  <button
                    type="submit"
                    className="btn btn-secondary"
                    onClick={toggleNewTemplate}
                  >
                    {t("common.button.newTemplate")}
                  </button>
                </div>
              </div>
            </div>
            <div className="email-result-list px-4 py-3">
              <h3>{t("auth.email.template.title")}</h3>
              <ul>
                {categoryList &&
                  categoryList.length > 0 &&
                  categoryList.map((cat, idx) => (
                    <li
                      className={
                        activeCategory === cat.category ? "active" : ""
                      }
                      key={`${cat.category}-${idx}`}
                    >
                      <button onClick={() => toggleCategory(cat.category)}>
                        {cat.category}
                      </button>
                    </li>
                  ))}
              </ul>
            </div>
            {!activeTemplate && !formMode && (
              <div className="email-list">
                <ul>
                  {emailTemplateList &&
                    emailTemplateList.length > 0 &&
                    emailTemplateList.map((template, index) => (
                      <li className="relative" key={`${template.gid}-${index}`}>
                        <div className="read-view absolute right-0 me-4">
                          <button
                            type="button"
                            className="button wish"
                            onClick={() => onWishListTemplate(template)}
                          >
                            <i
                              className={
                                template.wishlist
                                  ? "bx bxs-heart"
                                  : "bx bx-heart"
                              }
                            ></i>
                          </button>
                          <button
                            type="button"
                            className="button copy"
                            onClick={() => toggleCopyTemplate(template)}
                          >
                            <i className="bx bx-copy"></i>
                          </button>
                          <button
                            type="button"
                            className="button wish"
                            onClick={() => onDeleteTemplate(template.gid)}
                          >
                            <i className="bx bx-trash"></i>
                          </button>
                        </div>
                        <div
                          className={
                            template.gid ===
                            (activeTemplate && activeTemplate.gid)
                              ? "active"
                              : ""
                          }
                          key={`${template.gid}${index}`}
                          onClick={() => onViewTemplate(template.gid)}
                        >
                          <div className="email-list-box d-flex align-items-start">
                            <div className="position-relative">
                              {template.photoURL ? (
                                <div className="w-8 h-8 flex-none rounded-circle overflow-hidden me-2">
                                  <OverlayTrigger
                                    placement="right"
                                    overlay={
                                      <Popover className="max-w-unset">
                                        <UserHover user={template} />
                                      </Popover>
                                    }
                                  >
                                    {template.photoURL ? (
                                      <img
                                        src={template.photoURL}
                                        alt={`${template.firstName} ${template.lastName}`}
                                        className="w-100 h-100 object-fit-cover"
                                      />
                                    ) : (
                                      getFirstLetterOfName(template)
                                    )}
                                  </OverlayTrigger>
                                </div>
                              ) : (
                                getFirstLetterOfName(template)
                              )}
                            </div>
                            <div className="email-short-details">
                              <div className="emailer-name">
                                {template.firstName} {template.lastName}
                              </div>
                              <div className="email-title">{template.name}</div>
                              <div className="email-short-content">
                                <p>{template.description}</p>
                              </div>
                              <div className="email-status-list">
                                <ul>
                                  <li>
                                    <i className="bx bx-heart"></i>{" "}
                                    {template.likes} {t("common.button.likes")}
                                  </li>
                                  <li>
                                    <i className="bx bx-clipboard"></i>{" "}
                                    {template.usages}
                                    {t("common.button.timesUsed")}
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </div>
        )}
        {/* /#Email Template List */}

        {/* View/Edit Template Detail */}
        {activeTemplate && !formMode && (
          <div className="email-details">
            <div className="email-details-box bg-white overflow-hidden">
              <div className="email-header px-5 py-3 bg-light">
                <div className="d-flex align-items-center">
                  <button
                    className="btn btn-back btn-tranparent d-flex justify-content-center align-items-center border-0 rounded-circle"
                    onClick={onResetTemplate}
                  >
                    <i className="bx bx-arrow-back bx-sm"></i>
                  </button>
                  <h3 className="h6 ms-1 mb-0">{activeTemplate.name}</h3>
                </div>
              </div>

              <div className="email-body p-3">
                <div className="d-flex mb-3 hover-effect position-relative cursor-pointer">
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Popover className="max-w-unset">
                        <UserHover user={activeTemplate} />
                      </Popover>
                    }
                  >
                    <div className="d-flex align-items-center">
                      <div className="w-10 h-10 rounded-circle overflow-hidden">
                        {activeTemplate.photoURL ? (
                          <div className="img-box w-100 h-100">
                            <img
                              src={activeTemplate.photoURL}
                              alt={`${activeTemplate.firstName}${activeTemplate.lastName}`}
                              className="w-100 h-100 object-fit-cover"
                            />
                          </div>
                        ) : (
                          getFirstLetterOfName(activeTemplate)
                        )}
                      </div>

                      <div className="avatar-short-dtls ms-2">
                        <h3 className="mb-0 text-sm font-semibold text-nowrap">
                          {activeTemplate.firstName} {activeTemplate.lastName}
                        </h3>
                        <div className="email-time text-xs font-medium text-slate-500">
                          {t("auth.email.template.contributed")}{" "}
                          {activeTemplate.createdDate &&
                            formatDateWithShortMonth(
                              activeTemplate.createdDate
                            )}
                        </div>
                      </div>
                    </div>
                  </OverlayTrigger>
                </div>

                <h3 className="mb-2 email-title">{activeTemplate.category}</h3>
                <h3 className="mb-2 email-title">
                  {activeTemplate.description}
                </h3>
                <div className="email-content-area max-h-500 overflow-y-auto">
                  <div
                    className="email-content-paragraph"
                    dangerouslySetInnerHTML={{
                      __html: activeTemplate.template,
                    }}
                  />
                </div>

                {activeTemplate.files &&
                  activeTemplate.files.length > 0 &&
                  activeTemplate.files.map((ta, idx) => (
                    <div className="attache-box" key={`${ta.name}-${idx}`}>
                      <div className="attache-icon">
                        <i className="bx bxs-file-pdf"></i>
                      </div>
                      <div className="attache-details">
                        <div className="attach-filename">{ta.name}</div>
                        <small>{ta.size}</small>
                        <button
                          type="button"
                          className="btn-close close-icon"
                          onClick={() => downloadFile(ta.url, ta.name)}
                        >
                          <i className="bx bxs-download"></i>
                        </button>
                      </div>
                    </div>
                  ))}
              </div>

              <div className="email-footer px-3">
                <div className="row align-items-center">
                  <div className="col-auto ms-md-auto">
                    <ul className="email-btn-list">
                      <li>
                        <button
                          type="button"
                          className="btn btn-transparent"
                          onClick={onResetTemplate}
                        >
                          {t("common.button.cancel")}
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => onEditTemplate(activeTemplate)}
                        >
                          <i className="bx bx-pencil"></i>{" "}
                          {t("common.button.edit")}
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* /#View/Edit Template */}

        {/* Email Template Form - Create Mode */}
        {formMode === "create" && (
          <EmailTemplateForm
            initialData={null}
            variableList={variableList}
            variableListForEmailEdtor={variableListForEmailEdtor}
            headerTitle={t("auth.email.template.createNew")}
            isLoading={buttonLoading}
            onSubmit={onAddTemplate}
            onCancel={onResetTemplate}
          />
        )}

        {/* Email Template Form - Edit Mode */}
        {formMode === "edit" && (
          <EmailTemplateForm
            variableList={variableList}
            variableListForEmailEdtor={variableListForEmailEdtor}
            headerTitle={t("auth.email.template.editTemplate")}
            isLoading={buttonLoading}
            initialData={editTemplateData}
            onSubmit={onSaveTemplate}
            onCancel={onResetTemplate}
          />
        )}
      </div>

      {isCopyTemplate && (
        <CopyTemplate
          show={isCopyTemplate}
          toggle={toggleCopyTemplate}
          onSubmit={onCopyTemplate}
          data={copyTemplateData}
        />
      )}
    </LoadingComponent>
  );
};
